import { useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  invalidateMozartNotifications,
  logAnalyticMozartDefectAlert,
  reloadStateNotificationsNew,
  setNotificationSeenById,
} from 'features/mozart-notifications/infra';
import appActions from 'redux/actions/app-actions/app.actions';
import { setTurnOnProductByStoreId } from 'features/menu-page/infra'; // menu
import { useMozartDefectSolvedLang } from './useMozartDefectSolvedLang';

import {
  MozartComplaint,
  SolutionModeOption,
} from 'features/mozart-notifications/domain';
import type { ChangeEvent } from 'react';
import type { ModalProps } from '@material-ui/core/Modal';

const OPTION_INIT: SolutionModeOption | undefined = undefined;

const MAX_LENGTH = 200;

export function useMozartDefectSolved(props: Props) {
  const { complaint, onClose = () => {} } = props;

  const dispatchRedux = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [textareaValue, setTextareaValue] = useState('');
  const [selectedOption, setSelectedOption] = useState(OPTION_INIT);

  const lang = useMozartDefectSolvedLang({ productName: complaint.title });

  const isDefectWarning = complaint.reason === 'DEFECT_WARNING';
  const isOtherOption = selectedOption === 'other';

  const handleOptionChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSelectedOption(event.target.value as SolutionModeOption);
  };

  const handleTextareaChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setTextareaValue(event.target.value);
  };

  const sendSeenAndAnalytic = () => {
    setNotificationSeenById({
      id: complaint.id,
      createdAt: complaint.createdAt,
    }).then();
    logAnalyticMozartDefectAlert({
      complaint,
      action: isDefectWarning ? 'solved' : 'turn_on_product',
      solutionType: selectedOption,
      solutionOpenBox: textareaValue,
    });
  };

  const _handleClose = (isSolved: boolean) => {
    setTimeout(() => {
      setIsLoading(false);
      invalidateMozartNotifications();
      reloadStateNotificationsNew();
      dispatchRedux(
        appActions.openSnackBarSuccess(
          lang.snackBar.success[isSolved ? 'solved' : 'turnOnProduct'].msg,
          lang.snackBar.success[isSolved ? 'solved' : 'turnOnProduct'].title,
          5_000,
        ),
      );
      onClose({}, 'escapeKeyDown');
    }, 1_000);
  };

  const handleOnClickSend = () => {
    setIsLoading(true);
    if (isDefectWarning) {
      sendSeenAndAnalytic();
      _handleClose(true);
    } else {
      setTurnOnProductByStoreId({
        storeId: complaint.storeId,
        productId: complaint.productId,
      })
        .then(_ => {
          sendSeenAndAnalytic();
          _handleClose(false);
        })
        .catch(_ => {
          setIsLoading(false);
          dispatchRedux(
            appActions.openSnackBarError(
              lang.snackBar.error.msg,
              lang.snackBar.error.title,
              5_000,
            ),
          );
        });
    }
  };

  return {
    lang,
    isLoading,
    isDisabled: !selectedOption || isLoading,
    isOtherOption,
    textareaValue,
    MAX_LENGTH,
    handleOnClickSend,
    handleOptionChange,
    handleTextareaChange,
  };
}

interface Props {
  complaint: MozartComplaint;
  onClose: ModalProps['onClose'];
}
